import { useEffect, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { IssuesListing, IssuesListingProps } from 'Components/Issues/IssuesListing/IssuesListing';
import { AssociatedControlsModal } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { IssueBreadcrumbSource, IssueResponse } from 'Models/Issues';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { UserResponse } from 'Models/User';

export interface IssuesTabOperationalControlsProps {
    issuesType: ProductModule.CONTROLS;
    issuesApi: IssuesApi;
    documentApi: DocumentApi;
    users: UserResponse[];
}

export interface IssuesTabDueDiligenceProps {
    issuesType: DueDiligenceModule;
    issuesApi: IssuesApi;
    documentApi: DocumentApi;
    users: UserResponse[];
    organizations: OrganizationResponseWithAssessmentSubjectsBase[];
    organizationIdQueryParam?: string;
}

export type IssuesTabProps = IssuesTabOperationalControlsProps | IssuesTabDueDiligenceProps;

/**
 * Renders a table of issues after fetching them via the API.
 */
export const IssuesTab = (props: IssuesTabProps) => {
    const [issues, setIssues] = useState<IssueResponse[]>();
    const [mappedControlsToDisplay, setMappedControlsToDisplay] = useState<OperationalControl[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    useEffect(() => {
        const getIssues = async (): Promise<void> => {
            try {
                const response = props.issuesType === ProductModule.CONTROLS ? await props.issuesApi.getAllControlIssues() : await props.issuesApi.getAllDueDiligenceIssues();
                setIssues(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getIssues();
    }, [props.issuesApi, props.issuesType]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (!issues) {
        return <Placeholder />;
    }

    const issuesListingProps: IssuesListingProps = {
        ...(props.issuesType === ProductModule.CONTROLS
            ? {
                  type: ProductModule.CONTROLS,
                  issues: issues as IssueResponse[],
                  displayMappedControlsModal: setMappedControlsToDisplay,
                  createButtonVariant: 'primaryButton',
              }
            : {
                  type: props.issuesType,
                  issues: issues as IssueResponse[],
                  documentApi: props.documentApi,
                  organizations: props.organizations,
                  preselectedOrganizationIdForFilter: props.organizationIdQueryParam,
                  createButtonVariant: 'primaryButton',
              }),
        createIssueOrigin: { source: IssueBreadcrumbSource.ISSUES_LISTING, module: props.issuesType },
        hideTitle: true,
    };

    return (
        <>
            {mappedControlsToDisplay && <AssociatedControlsModal associatedControls={mappedControlsToDisplay} hideModal={() => setMappedControlsToDisplay(undefined)} />}
            <IssuesListing {...issuesListingProps} />
        </>
    );
};
